Function.prototype.memoized = function () {
	let key = JSON.stringify(arguments)

	this._cache = this._cache || {}

	if (this._cache[key] !== undefined) {
		// console.log('FROM CACHE')
		return this._cache[key]
	} else {
		// console.log('NOT FROM CACHE')
		const functionCall = this.apply(this, arguments)

		if (functionCall?.then) {
			functionCall.then((result) => (this._cache[key] = result))
		} else {
			this._cache[key] = functionCall
		}

		return functionCall
	}
}

Function.prototype.memoize = function () {
	let fn = this

	if (fn.length === 0) {
		return fn
	}

	return function () {
		return fn.memoized.apply(fn, arguments)
	}
}

Array.prototype.asyncMap = async function (callback) {
	const callNext = async (resultArray, index) => {
		if (index >= this.length) {
			return resultArray
		}

		const result = await callback(this[index], index)

		return callNext([...resultArray, result], index + 1)
	}

	return new Promise((resolve) => {
		callNext([], 0)
			.then((result) => resolve(result))
			.catch((error) => console.error(error))
	})
}

export function stringifyData(data) {
	if (!['string', 'number', 'undefined'].includes(typeof data)) {
		return JSON.stringify(data)
	} else {
		return data
	}
}

export function parseData(data) {
	if (['undefined'].includes(typeof data)) {
		return data
	} else {
		return JSON.parse(data)
	}
}

export function arrayToObjectByKey(key, data) {
	return Object.fromEntries(data.map((item) => [item[key], item]))
}

export function clearObjectAttrs(attrs, data) {
	return Object.fromEntries(
		Object.entries(data).filter(([attrName, value]) => !attrs.includes(attrName)),
	)
}

// Префикс служебных полей, которые composable сущности добавляет к объекту в
// postProcessEntityCallback: заголовок для интерфейса, разрешённые справочники.
export const SERVICE_ATTR_PREFIX = '__ao__'

// Срезает служебные поля перед отправкой объекта на бэкенд. По префиксу, а не по
// перечню: состав таких полей задаёт каждая сущность своим postProcessEntityCallback,
// и перечень пришлось бы обновлять при каждой правке колбэка.
export function clearObjectServiceAttrs(data) {
	return Object.fromEntries(
		Object.entries(data).filter(([attrName]) => !attrName.startsWith(SERVICE_ATTR_PREFIX)),
	)
}

export function clearEmptyObjectFields(obj, recursionCall = false) {
	let firstLevelClearObject = Object.fromEntries(
		Object.entries(obj).filter(([key, value]) => !(typeof value === undefined || value === null)),
	)
	let fullyClearObject = null

	if (recursionCall) {
		fullyClearObject = Object.fromEntries(
			Object.entries(firstLevelClearObject).map(([key, value]) => {
				if (typeof value === 'object') {
					return [key, clearEmptyObjectFields(value, true)]
				} else {
					return [key, value]
				}
			}),
		)
	}

	return recursionCall ? fullyClearObject : firstLevelClearObject
}

export function promiseWrap(func, funcArgs = []) {
	return new Promise(async (resolve, reject) => {
		try {
			const result = await func(...funcArgs)
			resolve(result)
		} catch (error) {
			reject(error)
		}
	})
}

export function removeEmptyObjectAttrs(data, emptyCondition = null) {
	return Object.fromEntries(Object.entries(data).filter(([_, v]) => v != emptyCondition))
}

export function iterateObjectValues(object, callback) {
	return Object.fromEntries(
		Object.entries(object).map(([key, value]) => [key, callback(key, value)]),
	)
}

export function set(object, key, value) {
	return (object[key] = value)
}

export function getRndInteger(min, max) {
	return Math.floor(Math.random() * (max - min)) + min
}

export function moveArrayItems(array, indexFrom, indexTo) {
	array.splice(indexTo, 0, array.splice(indexFrom, 1)[0])
}

export function copyObject(object) {
	return JSON.parse(JSON.stringify(object))
}

export function round(number) {
	return Math.round(parseFloat(number) * 100) / 100
}

export function currencyFormat(value, currency) {
	let currencyToSet = currency

	const exceptions = {
		грн: 'UAH',
	}

	if (exceptions[currency]) {
		currencyToSet = exceptions[currency]
	}

	return value.toLocaleString('uk-UA', { style: 'currency', currency: currencyToSet })
}

export function createMicroTask(callback, ms = 0) {
	return setTimeout(() => {
		callback()
	}, ms)
}

export function setExtraErrors(state, error) {
	state.extraErrors = Object.fromEntries(
		Object.entries(error?.response?.data || {}).map(([key, errors]) => [
			key,
			Array.isArray(errors) ? errors.join(' ') : errors,
		]),
	)
}

export async function sendRequestsByPieces(
	requestFunctionsArr = [],
	result = { executed: 0, responses: [] },
) {
	const maxRequestCountPerTime = 19
	const recursionCall = requestFunctionsArr.length > maxRequestCountPerTime

	result.responses = []

	const promises = requestFunctionsArr.slice(0, maxRequestCountPerTime).map(
		(requestFunc) =>
			new Promise((res, rej) => {
				requestFunc()
					.then((response) => ((result.executed += 1), result.responses.push(response), res()))
					.catch(() => res())
			}),
	)

	if (recursionCall) {
		await Promise.allSettled(promises)

		return sendRequestsByPieces(requestFunctionsArr.slice(19), result)
	} else {
		await Promise.allSettled(promises)

		return [...result.responses]
	}
}

// export function createItemNameArrayById(idArray, objectArray, key = 'name') {
// 	// const entityArray = (idArray || []).map(itemId => objectArray?.find((el) => el.id === itemId))
// 	// 		return entityArray?.map(item => item[key])
// 	return (idArray || [])
// 		.map((itemId) => objectArray?.find((el) => el.id === itemId))
// 		.map((item) => item?.[key])
// 		.filter((item) => item)
// }

export async function svgToBase64(url) {
	try {
		const svgString = await (await fetch(url)).text()

		return svgStringToBase64(svgString)
	} catch (error) {
		console.error(error)
		return ''
	}
}

export function svgStringToBase64(svgString) {
	return `data:image/svg+xml;base64,${window.btoa(svgString)}`
}

export function hexToRgbaArray(hexCode, opacity = 1) {
	if (!hexCode) return [0, 0, 0, opacity * 255]

	let hex = hexCode.replace('#', '')

	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
	}

	let r = parseInt(hex.substring(0, 2), 16),
		g = parseInt(hex.substring(2, 4), 16),
		b = parseInt(hex.substring(4, 6), 16)

	return [r, g, b, opacity * 255]
}

export function rgbArrayToHex(rgbArray) {
	return (
		'#' +
		rgbArray
			.slice(0, 3)
			.map((c) => c.toString(16).padStart(2, '0'))
			.join('')
	)
}

export function unicodeToChar(text) {
	return text.replace(/\\u[\dA-F]{4}/gi, function (match) {
		return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
	})
}
export function createItemNameArrayById(idArray, objectArray, key = 'name') {
	const entityArray = idArray?.map((itemId) => objectArray?.find((el) => el.id === itemId))

	return entityArray?.map((item) => item?.[key])
}

export const getAngle = ([x1, y1], [x2, y2]) => (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI

export function determineTone(hex) {
	let r, g, b

	// Check if hex starts with #
	if (hex[0] === '#') {
		hex = hex.substring(1)
	}

	// Convert hex to RGB values
	if (hex.length === 3) {
		r = parseInt(hex[0] + hex[0], 16)
		g = parseInt(hex[1] + hex[1], 16)
		b = parseInt(hex[2] + hex[2], 16)
	} else if (hex.length === 6) {
		r = parseInt(hex.substring(0, 2), 16)
		g = parseInt(hex.substring(2, 4), 16)
		b = parseInt(hex.substring(4, 6), 16)
	} else {
		return 'light' // default value
	}

	// Determine the tone of the color
	let luma = r * 0.299 + g * 0.587 + b * 0.114
	return luma > 186 ? 'light' : 'dark'
}

import * as d3 from 'd3'

export function svgStringFromPath(pathArray, { fill = '#000', stroke = '#000' }) {
	// create a temporary SVG element and append it to the document body
	const tempSvg = d3.create('svg').attr('style', 'position:absolute; left:-9999px; top:-9999px;')
	document.body.appendChild(tempSvg.node())

	// create an empty group element to hold the paths
	const group = tempSvg.append('g')

	// add each path to the group
	pathArray?.forEach((pathString) => {
		group
			.append('path')
			.attr('d', pathString)
			.attr('fill', fill)
			.attr('stroke', stroke)
			.attr('stroke-width', '0.5%')
			.attr('stroke-linecap', 'round')
			.attr('stroke-linejoin', 'round')
	})

	// calculate the bounding box of the group
	const bbox = group.node().getBBox()

	// remove the temporary SVG element from the document body
	document.body.removeChild(tempSvg.node())

	// create the final SVG element with the correct viewBox and add the paths
	const svg = d3.create('svg').attr('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`)
	group.selectAll('path').each(function () {
		svg.node().appendChild(this.cloneNode(true))
	})

	// return the SVG string
	return svg.node().outerHTML
}

// import * as geojson2svg from 'geojson2svg'
const geojson2svg = window.geojson2svg

export function geoJson2SVG(geoJson, attrs) {
	const converter = geojson2svg({
		output: 'path',
		explode: false,
		mapExtent: {
			left: -180,
			bottom: -90,
			right: 180,
			top: 90,
		},
		viewportSize: {
			width: 800,
			height: 800,
		},
	})

	const paths = converter.convert(geoJson)

	return svgStringFromPath(paths, attrs)
}

// global filter method
import { FILTER_TYPES_MAP } from '@/config/managers/filterManagerConfig'

export const doesExternalFilterPass = (data, filtersState, filterConfig, searchText) => {
	const filteringState = Object.entries(filtersState).map(([fieldName, filedFilterState]) => {
		const fieldFilterConfig = filterConfig.find(({ name }) => name === fieldName)
		const fieldValue = fieldFilterConfig.filterableValueGetter
			? fieldFilterConfig.filterableValueGetter(
					data,
					fieldFilterConfig.itemValue,
					fieldFilterConfig.itemTitle,
			  )
			: data?.[fieldName]

		if (!data || !fieldFilterConfig || typeof fieldValue === 'undefined') return true

		switch (fieldFilterConfig.type) {
			case FILTER_TYPES_MAP.SINGLE_CHOICE:
			case FILTER_TYPES_MAP.BOOLEAN:
				return fieldValue === filedFilterState
			case FILTER_TYPES_MAP.MULTIPLE_CHOICE:
				return Array.isArray(fieldValue)
					? fieldValue.some((v) => filedFilterState.includes(v))
					: filedFilterState.includes(fieldValue)
			case FILTER_TYPES_MAP.RANGE:
			case FILTER_TYPES_MAP.DATE_RANGE:
			case FILTER_TYPES_MAP.DURATION:
				if (filedFilterState[0] !== null && filedFilterState[1] !== null) {
					return fieldValue >= filedFilterState[0] && fieldValue <= filedFilterState[1]
				} else if (filedFilterState[0] === null) {
					return fieldValue <= filedFilterState[1]
				} else if (filedFilterState[1] === null) {
					return fieldValue >= filedFilterState[0]
				} else {
					return true
				}
			case FILTER_TYPES_MAP.PERIOD:
				return Array.isArray(fieldValue)
					? fieldValue[0] >= filedFilterState[0] && fieldValue[1] <= filedFilterState[1]
					: false
			case FILTER_TYPES_MAP.SEARCH:
				return fieldValue?.toLowerCase()?.includes(filedFilterState?.toLowerCase() || '')
			default:
				return true
		}
	})

	if (data?.aoSearchField && searchText) {
		filteringState.push(data.aoSearchField.toLowerCase().includes(searchText.toLowerCase()))
	}
	// console.log('================')

	return filteringState.every((filteringResult) => filteringResult)
}

export function getTextFromHTML(htmlString) {
	// Create a temporary div element to hold the HTML string
	var tempDiv = document.createElement('div')
	tempDiv.innerHTML = htmlString

	// Extract the text content and replace <br> tags with newline characters
	var textContent = tempDiv.textContent || tempDiv.innerText || ''
	textContent = textContent.replace(/<br\s*[\/]?>/gi, '\n')

	// Clean up the temporary div and return the result
	tempDiv = null
	return textContent
}

export function sortArray(data, config) {
	if (!Array.isArray(data)) {
		throw new Error('Data must be an array')
	}

	if (typeof config !== 'object' || Array.isArray(config)) {
		throw new Error('Config must be an object')
	}

	const keys = Object.keys(config)

	if (keys.length === 0) {
		return [...data]
	}

	const sortedData = [...data]

	sortedData.sort((a, b) => {
		let comparison = 0

		for (const key of keys) {
			const sortOrder = config[key]

			if (sortOrder === '' || sortOrder === undefined) {
				continue
			}

			const aValue = a[key]
			const bValue = b[key]

			if (aValue === '' && bValue === '') {
				comparison = 1
				break
			}

			if (aValue === null || aValue === undefined) {
				comparison = -1
				break
			}

			if (bValue === null || bValue === undefined) {
				comparison = 1
				break
			}

			if (typeof aValue === 'string' && typeof bValue === 'string') {
				comparison = aValue.localeCompare(bValue)
			} else {
				comparison = aValue - bValue
			}

			if (sortOrder === 'desc') {
				comparison = -comparison
			}

			if (comparison !== 0) {
				break
			}
		}

		return comparison
	})

	return sortedData
}
export function sortMetricsByPriority(arr) {
	return arr.sort((a, b) => {
		if (a.is_main && !b.is_main) {
			return -1
		} else if (!a.is_main && b.is_main) {
			return 1
		} else if (a.is_base && !b.is_base) {
			return -1
		} else if (!a.is_base && b.is_base) {
			return 1
		} else {
			return 0
		}
	})
}

import { findLastDate } from '@/helpers/dateHelper'

export function getIdentificationDataByIdent(mountsArray, data) {
	const identificationData = mountsArray?.filter(
		({ on_date }) => new Date(on_date) < new Date(data),
	)

	return findLastDate(identificationData, 'on_date')
}

export function safeSyncCall(fn) {
	try {
		return [null, fn()]
	} catch (e) {
		console.error(e)

		return [e, null]
	}
}

/**
 * @deprecated use formatTechnic
 * @param {array} machines
 * @param {string} regNumber
 * @returns {array}
 */
export function createNameMachinesAndEquipment(machines, regNumber) {
	return machines?.map((machine) => ({
		...machine,
		name: machine?.[regNumber]
			? `${machine?.brand} ${machine?.model ? machine?.model : ''} (${machine?.[regNumber]})`
			: `${machine?.brand} ${machine?.model ? machine?.model : ''}`,
	}))
}

export function adjustColor(rgbColor) {
	const [r, g, b] = rgbColor

	// Calculate the luminance of the color
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

	// Define the adjustment factor based on luminance
	const adjustment = luminance > 0.5 ? -0.1 : 0.1

	// Adjust each color component
	const adjustedR = Math.max(0, Math.min(255, Math.round(r + adjustment * r)))
	const adjustedG = Math.max(0, Math.min(255, Math.round(g + adjustment * g)))
	const adjustedB = Math.max(0, Math.min(255, Math.round(b + adjustment * b)))

	return [adjustedR, adjustedG, adjustedB]
}

export function formatNumber(number) {
	return new Intl.NumberFormat().format(parseFloat(number))
}

export function downloadByUrl(url, fileName, fileExtension) {
	let fileNameToDownload = fileName
	let fileExtensionToDownload = fileExtension

	if (!fileName) {
		fileNameToDownload = url.split('/').pop()
	}

	if (!fileExtension) {
		fileExtensionToDownload = fileNameToDownload.split('.').pop()

		if (!fileName) {
			fileNameToDownload = fileNameToDownload.split('.').shift()
		}
	}

	fetch(url)
		.then((response) => response.blob())
		.then((blob) => {
			const link = document.createElement('a')

			link.href = URL.createObjectURL(blob)
			link.download = `${fileNameToDownload}.${fileExtensionToDownload}`

			link.click()
			link.remove()
		})
		.catch(console.error)
}

export function isStringIntegerOrFloat(value) {
	return /^-?\d+\.?\d*$/.test(value)
}

export function findEquipmentByMachineGroups(machineGroups, equipmentList) {
	const equipmentGroups = machineGroups.reduce((acc, group) => {
		if (group.equipment_groups?.length) {
			group.equipment_groups.map((equipmentGroup) => {
				if (acc.includes(equipmentGroup)) return

				acc.push(equipmentGroup)
			})
		}

		return acc
	}, [])

	const equipmentsFiltered = equipmentList.filter((equipment) => {
		if (equipment.equipment_groups?.length) {
			return equipment.equipment_groups.some((group) => equipmentGroups.includes(group))
		}
	})

	return equipmentsFiltered
}

export function formatTechnic(object, isModel = true, isNumber = true) {
	let title = ''
	if (object.brand) {
		title += object.brand
	}
	if (object.model && isModel) {
		title += ` ${object.model}`
	}
	if (object?.registration_number && isNumber) {
		title += ` (${object.registration_number})`
	}
	if (object?.official_number && isNumber) {
		title += ` (${object.official_number})`
	}
	return title || null
}

export async function selectFile(extensions = []) {
	return new Promise((resolve) => {
		const input = document.createElement('input')
		input.type = 'file'
		input.accept = extensions.map((e) => `.${e}`).join(', ')
		input.onchange = (event) => {
			const file = event.target.files[0]
			resolve(file)
		}

		input.click()
	})
}
export function scrollToElement(selectorName) {
	const element = document.querySelector(selectorName)
	element.focus()
	element.scrollIntoView({ behavior: 'smooth' })
}

/**
 * Safely retrieves the value of a nested property from an object using a dot-separated path.
 * @param {Object} obj - The object from which to retrieve the value.
 * @param {string} path - The dot-separated path to the desired property (e.g., 'foo.bar').
 * @returns {*} - The value of the nested property, or undefined if any part of the path is invalid.
 */
const getNestedProperty = (obj, path) => {
	return path.split('.').reduce((acc, key) => acc && acc[key], obj)
}

/**
 * Группирует свойства плоского объекта во вложенную структуру,
 * используя указанный разделитель в ключах.
 *
 * @param {object} flatObject - Исходный плоский объект.
 * @param {string} [delimiter='.'] - Символ или строка-разделитель в ключах. По умолчанию '.'.
 * @returns {object} - Новый объект с вложенной структурой.
 */
const groupPropertiesByDelimiter = (flatObject, delimiter = '.') => {
	const result = {}

	// Проверяем, что исходный объект не является null или undefined
	if (flatObject === null || typeof flatObject !== 'object') {
		return result
	}

	for (const key in flatObject) {
		// Проверяем, что ключ принадлежит самому объекту, а не его прототипу
		if (Object.prototype.hasOwnProperty.call(flatObject, key)) {
			const value = flatObject[key]
			// Используем переданный разделитель для разделения ключа
			const parts = key.split(delimiter)

			let currentLevel = result

			// Проходим по всем частям ключа, кроме последней, чтобы создать вложенные объекты
			for (let i = 0; i < parts.length - 1; i++) {
				const part = parts[i]
				// Если на текущем уровне вложенности нет такого объекта или он не является объектом, создаем его
				if (!currentLevel[part] || typeof currentLevel[part] !== 'object') {
					currentLevel[part] = {}
				}
				// Переходим на следующий уровень вложенности
				currentLevel = currentLevel[part]
			}

			// На последнем уровне вложенности присваиваем значение
			const lastPart = parts[parts.length - 1]
			currentLevel[lastPart] = value
		}
	}

	return result
}

/**
 * Иммутабельно устанавливает значение во вложенном объекте по строковому пути.
 * Возвращает новый объект, не мутируя исходный.
 * @param {object} obj - Исходный объект.
 * @param {string} path - Путь для установки значения (например, 'user.profile.name').
 * @param {*} value - Новое значение.
 * @returns {object} - Новый объект с обновленным значением.
 */
const setImmutableValueByPath = (obj, path, value) => {
	if (typeof path !== 'string' || path.length === 0) {
		console.error('setImmutableValueByPath: path должен быть непустой строкой')
		return obj // Возвращаем исходный объект, если путь некорректен
	}

	const parts = path.split('.')
	const newObj = copyObject(obj)

	let current = newObj
	for (let i = 0; i < parts.length - 1; i++) {
		const part = parts[i]
		if (
			current[part] === undefined ||
			typeof current[part] !== 'object' ||
			current[part] === null
		) {
			current[part] = {}
		}
		current = current[part]
	}

	current[parts[parts.length - 1]] = value
	return newObj
}

export { getNestedProperty, groupPropertiesByDelimiter, setImmutableValueByPath }
